import {distinctUntilChanged, filter, map, of, switchMap, take, timer, type Observable} from "rxjs";

/**
 * Turns the raw "a load is open" state into "show the loading bar" (ticket AaDIdeKTd4mzxpHJI8SoCA).
 *
 * Two stages:
 * 1. Idle gaps shorter than `gapMs` count as loading. `@o2d/datasource` fetches regions one after
 *    another, so between one query's end and the next one's start the count touches 0 for a moment;
 *    without this stage a shown bar would hide there, and a drag made of many short queries would
 *    restart the show delay every time and never show a bar.
 * 2. The bar appears once stage 1 has been busy for `showMs`, and only at a moment when a load is
 *    really open — not during stage 1's after-run, which would otherwise flash the bar for loads
 *    slightly shorter than `showMs`.
 *
 * The bar hides `gapMs` after the last load ends.
 *
 * @param busy$ - the raw state; must replay its current value to new subscribers (BehaviorSubject)
 * @param showMs - how long loading must last before the bar shows
 * @param gapMs - idle gaps shorter than this count as loading; also the hide delay
 */
export function delayedBusy(busy$: Observable<boolean>, showMs = 150, gapMs = 50): Observable<boolean> {
    return busy$.pipe(
        switchMap(busy => busy ? of(true) : timer(gapMs).pipe(map(() => false))),
        distinctUntilChanged(),
        switchMap(busy => busy
            ? timer(showMs).pipe(
                switchMap(() => busy$.pipe(filter(Boolean), take(1))),
                map(() => true),
            )
            : of(false)),
        distinctUntilChanged(),
    );
}
